import { request } from '@/common/services/request';
import type {
  KlineBar,
  MarketInfo,
  MarketStatus,
  PlatformKlineItem,
  PlatformKlinesMessage,
  Quote,
} from '@/types/api';

export type {
  KlineBar,
  MarketInfo,
  MarketStatus,
  PlatformKlineItem,
  PlatformKlinesMessage,
  Quote,
  MarketStage,
} from '@/types/api';

export function unwrapMarketList(raw: unknown): MarketInfo[] {
  if (Array.isArray(raw)) return raw as MarketInfo[];
  if (raw && typeof raw === 'object') {
    const items = (raw as { items?: unknown }).items;
    if (Array.isArray(items)) return items as MarketInfo[];
  }
  return [];
}

/** 短缓存 + 进行中合并，避免列表页/合约页叠打被浏览器取消 */
let marketsInFlight: Promise<MarketInfo[]> | null = null;
let marketsCache: { at: number; data: MarketInfo[] } | null = null;
const MARKETS_TTL_MS = 5_000;

/** GET /v1/markets — 兼容数组或 { items } */
export const getMarkets = async (): Promise<MarketInfo[]> => {
  if (marketsCache && Date.now() - marketsCache.at < MARKETS_TTL_MS) {
    return marketsCache.data;
  }
  if (marketsInFlight) return marketsInFlight;
  marketsInFlight = (async () => {
    try {
      const raw = await request<unknown>({
        url: '/v1/markets',
        method: 'GET',
        skipAuth: true,
      });
      const data = unwrapMarketList(raw);
      marketsCache = { at: Date.now(), data };
      return data;
    } finally {
      marketsInFlight = null;
    }
  })();
  return marketsInFlight;
};

/** GET /v1/market/status */
export const getMarketStatus = (): Promise<MarketStatus> => {
  return request({
    url: '/v1/market/status',
    method: 'GET',
    skipAuth: true,
  });
};

/** GET /v1/quotes/:symbol — Business 最新成交价（展示用；订单价以后端订单响应为准） */
export const getQuote = (symbol: string): Promise<Quote> => {
  return request({
    url: `/v1/quotes/${encodeURIComponent(symbol)}`,
    method: 'GET',
    skipAuth: true,
  });
};

export interface GetKlinesParams {
  interval?: '1m' | '5m' | string;
  limit?: number;
  /**
   * 平台图表必须显式传 PLATFORM；省略仍走旧真实行情契约。
   */
  priceSource?: 'PLATFORM' | string;
  /** 旧真实行情契约；与 priceSource 互斥优先 priceSource */
  priceType?: 'LAST_PRICE' | string;
}

/**
 * GET /v1/klines/:symbol
 * - priceSource=PLATFORM → PlatformKlinesMessage（含 items）
 * - 否则 → 旧契约（可能是 KlineBar[] 或带 items 的包装）
 */
export const getKlines = (
  symbol: string,
  params: GetKlinesParams = {}
): Promise<PlatformKlinesMessage | KlineBar[]> => {
  const data: Record<string, string | number> = {
    interval: params.interval ?? '1m',
    limit: params.limit ?? 100,
  };
  if (params.priceSource) {
    data.priceSource = params.priceSource;
  } else {
    data.priceType = params.priceType ?? 'LAST_PRICE';
  }

  return request({
    url: `/v1/klines/${encodeURIComponent(symbol)}`,
    method: 'GET',
    data,
    skipAuth: true,
    showError: false,
  });
};

/** 平台历史 K 线：固定 PLATFORM + 1m */
export const getPlatformKlines = (
  symbol: string,
  params: { limit?: number } = {}
): Promise<PlatformKlinesMessage> => {
  return getKlines(symbol, {
    interval: '1m',
    limit: params.limit ?? 200,
    priceSource: 'PLATFORM',
  }) as Promise<PlatformKlinesMessage>;
};

export function isPlatformKlinesMessage(
  raw: unknown
): raw is PlatformKlinesMessage {
  if (!raw || typeof raw !== 'object') return false;
  const m = raw as PlatformKlinesMessage;
  if (!Array.isArray(m.items)) return false;
  if (m.type != null && m.type !== 'klines') return false;
  if (m.priceSource != null && m.priceSource !== 'PLATFORM') return false;
  if (m.sequence != null && !/^\d+$/.test(String(m.sequence))) return false;
  return true;
}

/** 从平台/旧响应中取出 bar 列表 */
export function unwrapKlineItems(
  raw: PlatformKlinesMessage | KlineBar[] | null | undefined
): Array<PlatformKlineItem | KlineBar> {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (isPlatformKlinesMessage(raw)) return raw.items || [];
  return [];
}
